/**
 * SQL Schema Configuration for Query Compilation
 *
 * Provides table and column identifiers that the query compiler uses.
 * This allows the compiler to work with custom table names instead of
 * hard-coded defaults.
 */
import {
  type EngineRecordedRevision,
  type EngineRecordedTimeMembers,
} from "../../backend/capabilities/recorded-time";
import { type VectorIndexType, type VectorMetric } from "../../backend/types";
import { MAX_PG_IDENTIFIER_LENGTH } from "../../constants";
import {
  parseRecordedInstant,
  type RecordedInstantParts,
} from "../../core/temporal";
import { CompilerInvariantError, ConfigurationError } from "../../errors";
import { typeGraphGlobalSymbol } from "../../utils/global-symbol";
import { isSqlFragment, sql, type SqlFragment } from "../sql-fragment";

const SQL_SCHEMA_BRAND: unique symbol = typeGraphGlobalSymbol("sql-schema-v1");

/**
 * Table names for TypeGraph SQL schema.
 *
 * Carries every customizable physical-table name the backend exposes,
 * including the secondary tables (`uniques`, `edgeClaims`, `fences`) that
 * the query compiler itself doesn't reference but `materializeRemovals` and
 * other cleanup paths (or, for `fences`, `resolveFenceStatements`'s
 * `row`-mechanism derivation) need to address by name. Backends without a
 * `uniques` table (custom embeddings-only stores) leave it as the
 * default — the cleanup path is a no-op for kinds with no unique
 * rows.
 */
export type SqlTableNames = Readonly<{
  /** Active schema version relation; absent on backends without checked reads. */
  schemaVersions?: string | undefined;
  /** Nodes table name (default: "typegraph_nodes") */
  nodes: string;
  /** Edges table name (default: "typegraph_edges") */
  edges: string;
  /** Recorded node relation table name (default: "typegraph_recorded_nodes") */
  recordedNodes?: string | undefined;
  /** Recorded edge relation table name (default: "typegraph_recorded_edges") */
  recordedEdges?: string | undefined;
  /** Recorded-time commit clock table name (default: "typegraph_recorded_clock") */
  recordedClock?: string | undefined;
  /** Durable per-graph revision-origin table name (default: "typegraph_revision_origins") */
  revisionOrigins?: string | undefined;
  /** Identity assertion ledger (default: "typegraph_identity_assertions") */
  identityAssertions?: string | undefined;
  /** Recorded identity assertion relation */
  recordedIdentityAssertions?: string | undefined;
  /** Derived current identity closure */
  identityClosure?: string | undefined;
  /** Derived separation relation over identity classes */
  identitySeparation?: string | undefined;
  /** Node fulltext table name (default: "typegraph_node_fulltext") */
  fulltext: string;
  /** Node uniques table name (default: "typegraph_node_uniques") */
  uniques: string;
  /** Edge cardinality claim table name (default: "typegraph_edge_claims") */
  edgeClaims?: string | undefined;
  /**
   * Write-fence rows table name (default: "typegraph_fences") — the
   * never-dropped relation a `row`-mechanism write fence acquires a keyed
   * exclusion against. Part of the base schema on every backend, whether or
   * not any target ever declares `writeFence.mechanism: "row"`.
   */
  fences?: string | undefined;
}>;

export type ResolvedSqlTableNames = Readonly<{
  /** Active schema version relation; absent on backends without checked reads. */
  schemaVersions?: string;
  /** Nodes table name */
  nodes: string;
  /** Edges table name */
  edges: string;
  /** Recorded node relation table name */
  recordedNodes: string;
  /** Recorded edge relation table name */
  recordedEdges: string;
  /** Recorded-time commit clock table name */
  recordedClock: string;
  /** Durable per-graph revision-origin table name */
  revisionOrigins: string;
  identityAssertions: string;
  recordedIdentityAssertions: string;
  identityClosure: string;
  identitySeparation: string;
  /** Node fulltext table name */
  fulltext: string;
  /** Node uniques table name */
  uniques: string;
  /** Edge cardinality claim table name */
  edgeClaims: string;
  /** Write-fence rows table name */
  fences: string;
}>;

type SqlSchemaFields = Readonly<{
  /** Table names */
  tables: ResolvedSqlTableNames;
  /** Get a `SqlFragment` reference to the nodes table. */
  nodesTable: SqlFragment;
  /** Get a `SqlFragment` reference to the edges table. */
  edgesTable: SqlFragment;
  /** Get a `SqlFragment` reference to the recorded node relation. */
  recordedNodesTable: SqlFragment;
  /** Get a `SqlFragment` reference to the recorded edge relation. */
  recordedEdgesTable: SqlFragment;
  /** Get a `SqlFragment` reference to the recorded-time commit clock. */
  recordedClockTable: SqlFragment;
  /** Get a `SqlFragment` reference to the durable per-graph revision origins. */
  revisionOriginsTable: SqlFragment;
  /** Get a `SqlFragment` reference to the identity assertion ledger. */
  identityAssertionsTable: SqlFragment;
  /** Get a `SqlFragment` reference to the recorded identity assertion relation. */
  recordedIdentityAssertionsTable: SqlFragment;
  /** Get a `SqlFragment` reference to the derived identity closure. */
  identityClosureTable: SqlFragment;
  /** Get a `SqlFragment` reference to the derived identity separation relation. */
  identitySeparationTable: SqlFragment;
  /** Get a `SqlFragment` reference to the fulltext table. */
  fulltextTable: SqlFragment;
}>;

/**
 * SQL schema configuration for query compilation.
 * Contains table identifiers and utility methods for generating `SqlFragment` references.
 *
 * Branded and frozen by {@link createSqlSchema}; callers should not construct
 * schema-shaped objects by hand.
 */
export abstract class SqlSchema implements SqlSchemaFields {
  declare private readonly typeGraphSqlSchemaBrand: true;
  abstract readonly tables: ResolvedSqlTableNames;
  abstract readonly nodesTable: SqlFragment;
  abstract readonly edgesTable: SqlFragment;
  abstract readonly recordedNodesTable: SqlFragment;
  abstract readonly recordedEdgesTable: SqlFragment;
  abstract readonly recordedClockTable: SqlFragment;
  abstract readonly revisionOriginsTable: SqlFragment;
  abstract readonly identityAssertionsTable: SqlFragment;
  abstract readonly recordedIdentityAssertionsTable: SqlFragment;
  abstract readonly identityClosureTable: SqlFragment;
  abstract readonly identitySeparationTable: SqlFragment;
  abstract readonly fulltextTable: SqlFragment;
}

class SqlSchemaDescriptor extends SqlSchema {
  declare readonly [SQL_SCHEMA_BRAND]: true;
  readonly tables: ResolvedSqlTableNames;
  readonly nodesTable: SqlFragment;
  readonly edgesTable: SqlFragment;
  readonly recordedNodesTable: SqlFragment;
  readonly recordedEdgesTable: SqlFragment;
  readonly recordedClockTable: SqlFragment;
  readonly revisionOriginsTable: SqlFragment;
  readonly identityAssertionsTable: SqlFragment;
  readonly recordedIdentityAssertionsTable: SqlFragment;
  readonly identityClosureTable: SqlFragment;
  readonly identitySeparationTable: SqlFragment;
  readonly fulltextTable: SqlFragment;

  constructor(fields: SqlSchemaFields) {
    super();
    Object.defineProperty(this, SQL_SCHEMA_BRAND, {
      configurable: false,
      enumerable: false,
      value: true,
      writable: false,
    });
    this.tables = fields.tables;
    this.nodesTable = fields.nodesTable;
    this.edgesTable = fields.edgesTable;
    this.recordedNodesTable = fields.recordedNodesTable;
    this.recordedEdgesTable = fields.recordedEdgesTable;
    this.recordedClockTable = fields.recordedClockTable;
    this.revisionOriginsTable = fields.revisionOriginsTable;
    this.identityAssertionsTable = fields.identityAssertionsTable;
    this.recordedIdentityAssertionsTable =
      fields.recordedIdentityAssertionsTable;
    this.identityClosureTable = fields.identityClosureTable;
    this.identitySeparationTable = fields.identitySeparationTable;
    this.fulltextTable = fields.fulltextTable;
    Object.freeze(this);
  }
}

/**
 * Default table names matching the standard TypeGraph schema.
 */
const DEFAULT_TABLE_NAMES = {
  schemaVersions: "typegraph_schema_versions",
  nodes: "typegraph_nodes",
  edges: "typegraph_edges",
  recordedNodes: "typegraph_recorded_nodes",
  recordedEdges: "typegraph_recorded_edges",
  recordedClock: "typegraph_recorded_clock",
  revisionOrigins: "typegraph_revision_origins",
  identityAssertions: "typegraph_identity_assertions",
  recordedIdentityAssertions: "typegraph_recorded_identity_assertions",
  identityClosure: "typegraph_identity_closure",
  identitySeparation: "typegraph_identity_separation",
  fulltext: "typegraph_node_fulltext",
  uniques: "typegraph_node_uniques",
  edgeClaims: "typegraph_edge_claims",
  fences: "typegraph_fences",
} satisfies ResolvedSqlTableNames;

function resolveTableNames(
  names: Partial<SqlTableNames>,
): ResolvedSqlTableNames {
  return {
    nodes: names.nodes ?? DEFAULT_TABLE_NAMES.nodes,
    edges: names.edges ?? DEFAULT_TABLE_NAMES.edges,
    recordedNodes: names.recordedNodes ?? DEFAULT_TABLE_NAMES.recordedNodes,
    recordedEdges: names.recordedEdges ?? DEFAULT_TABLE_NAMES.recordedEdges,
    recordedClock: names.recordedClock ?? DEFAULT_TABLE_NAMES.recordedClock,
    revisionOrigins:
      names.revisionOrigins ?? DEFAULT_TABLE_NAMES.revisionOrigins,
    identityAssertions:
      names.identityAssertions ?? DEFAULT_TABLE_NAMES.identityAssertions,
    recordedIdentityAssertions:
      names.recordedIdentityAssertions ??
      DEFAULT_TABLE_NAMES.recordedIdentityAssertions,
    identityClosure:
      names.identityClosure ?? DEFAULT_TABLE_NAMES.identityClosure,
    identitySeparation:
      names.identitySeparation ?? DEFAULT_TABLE_NAMES.identitySeparation,
    fulltext: names.fulltext ?? DEFAULT_TABLE_NAMES.fulltext,
    uniques: names.uniques ?? DEFAULT_TABLE_NAMES.uniques,
    edgeClaims: names.edgeClaims ?? DEFAULT_TABLE_NAMES.edgeClaims,
    fences: names.fences ?? DEFAULT_TABLE_NAMES.fences,
    schemaVersions: names.schemaVersions ?? DEFAULT_TABLE_NAMES.schemaVersions,
  };
}

/**
 * Regex for valid SQL identifiers.
 * Must start with a letter or underscore.
 * Can contain letters, digits, underscores, and dollar signs.
 * Dollar signs are a PostgreSQL extension but commonly supported.
 */
const VALID_IDENTIFIER_PATTERN = /^[a-z_][a-z0-9_$]*$/i;

/**
 * Validates that a table name is a valid SQL identifier.
 *
 * @throws Error if the table name is invalid
 */
function validateTableName(name: string, label: string): void {
  if (!name || name.length === 0) {
    throw new ConfigurationError(`${label} table name cannot be empty`);
  }
  if (name.length > MAX_PG_IDENTIFIER_LENGTH) {
    throw new ConfigurationError(
      `${label} table name exceeds maximum length of ${MAX_PG_IDENTIFIER_LENGTH} characters`,
    );
  }
  if (!VALID_IDENTIFIER_PATTERN.test(name)) {
    throw new ConfigurationError(
      `${label} table name "${name}" is not a valid SQL identifier. ` +
        `Table names must start with a letter or underscore and contain only letters, digits, underscores, or dollar signs.`,
    );
  }
}

function freezeSqlSchema(fields: SqlSchemaFields): SqlSchema {
  return new SqlSchemaDescriptor(fields);
}

function isSqlSchema(schema: unknown): schema is SqlSchema {
  if (typeof schema !== "object" || schema === null) return false;
  const candidate = schema as Record<PropertyKey, unknown>;
  return (
    candidate[SQL_SCHEMA_BRAND] === true &&
    typeof candidate["tables"] === "object" &&
    candidate["tables"] !== null &&
    Object.isFrozen(candidate["tables"]) &&
    isSqlFragment(candidate["nodesTable"]) &&
    isSqlFragment(candidate["edgesTable"]) &&
    isSqlFragment(candidate["recordedNodesTable"]) &&
    isSqlFragment(candidate["recordedEdgesTable"]) &&
    isSqlFragment(candidate["recordedClockTable"]) &&
    isSqlFragment(candidate["revisionOriginsTable"]) &&
    isSqlFragment(candidate["fulltextTable"]) &&
    Object.isFrozen(candidate)
  );
}

export function requireSqlSchema(
  schema: unknown,
  surface = "SqlSchema",
): SqlSchema {
  if (isSqlSchema(schema)) return schema;
  throw new ConfigurationError(
    `${surface} must be created with createSqlSchema(...).`,
    { code: "INVALID_SQL_SCHEMA", surface },
    {
      suggestion:
        "Pass the result of createSqlSchema(...) instead of a plain schema-shaped object.",
    },
  );
}

/**
 * Creates a SqlSchema configuration from table names.
 *
 * Table names are validated to ensure they are valid SQL identifiers.
 * This prevents SQL injection and ensures compatibility across databases.
 *
 * @param names - Optional custom table names (defaults to standard names)
 * @returns SqlSchema configuration for query compilation
 * @throws Error if any table name is invalid
 *
 * @example
 * ```typescript
 * // Use default table names
 * const schema = createSqlSchema();
 *
 * // Use custom table names
 * const schema = createSqlSchema({
 *   nodes: "myapp_nodes",
 *   edges: "myapp_edges",
 *   fulltext: "myapp_fulltext",
 * });
 * ```
 */
export function createSqlSchema(names: Partial<SqlTableNames> = {}): SqlSchema {
  const tables = resolveTableNames(names);

  // Validate all table names
  validateTableName(tables.nodes, "nodes");
  validateTableName(tables.edges, "edges");
  validateTableName(tables.recordedNodes, "recordedNodes");
  validateTableName(tables.recordedEdges, "recordedEdges");
  validateTableName(tables.recordedClock, "recordedClock");
  validateTableName(tables.revisionOrigins, "revisionOrigins");
  validateTableName(tables.identityAssertions, "identityAssertions");
  validateTableName(
    tables.recordedIdentityAssertions,
    "recordedIdentityAssertions",
  );
  validateTableName(tables.identityClosure, "identityClosure");
  validateTableName(tables.identitySeparation, "identitySeparation");
  validateTableName(tables.fulltext, "fulltext");
  validateTableName(tables.uniques, "uniques");
  validateTableName(tables.edgeClaims, "edgeClaims");
  validateTableName(tables.fences, "fences");

  return freezeSqlSchema({
    tables: Object.freeze(tables),
    nodesTable: sql.identifier(tables.nodes),
    edgesTable: sql.identifier(tables.edges),
    recordedNodesTable: sql.identifier(tables.recordedNodes),
    recordedEdgesTable: sql.identifier(tables.recordedEdges),
    recordedClockTable: sql.identifier(tables.recordedClock),
    revisionOriginsTable: sql.identifier(tables.revisionOrigins),
    identityAssertionsTable: sql.identifier(tables.identityAssertions),
    recordedIdentityAssertionsTable: sql.identifier(
      tables.recordedIdentityAssertions,
    ),
    identityClosureTable: sql.identifier(tables.identityClosure),
    identitySeparationTable: sql.identifier(tables.identitySeparation),
    fulltextTable: sql.identifier(tables.fulltext),
  });
}

/**
 * The recorded/system-time relation a read coordinate may reconstruct from.
 *
 * TypeGraph's built-in capture relation is bound by `createStore(..., {
 * history: true })`. Hosts can also bind externally populated row-compatible
 * recorded relations through `recordedRelation({ schema })`. Keeping both as
 * explicit values separates the read contract from the write-capture mechanism
 * so future external/TMS-owned recorded relations can feed the same query
 * machinery without changing StoreView or ReadCoordinate.
 */
const EXTERNAL_RECORDED_READ_SOURCE: unique symbol = typeGraphGlobalSymbol(
  "external-recorded-read-source-v1",
);

/**
 * The relation a {@link RecordedReadSource} sources rows from: the two entity
 * tables the query compiler swaps to for a recorded read, and the identity
 * assertion ledger the identity reconstruction path consults.
 */
export type RecordedSourceTable = "nodes" | "edges" | "identityAssertions";

/**
 * The one seam every recorded read consults instead of spelling the relation
 * swap and the recorded-time interval itself.
 *
 * `source` names the relation (or table expression) holding `table`'s
 * recorded rows for `revision`. TypeGraph's own capture and external
 * bindings both return the matching recorded relation regardless of
 * `revision` — the relation carries every revision, and `predicate` narrows
 * it afterward. A binding whose `source` already scopes its rows to exactly
 * one revision (an engine-native temporal table expression, say) returns
 * `undefined` from `predicate` instead of re-spelling a redundant filter.
 *
 * `carriesInterval` names the other fact every binding-shape-aware reader
 * needs: whether `source`'s rows carry the `recorded_from`/`recorded_to`
 * columns a TypeGraph-relation-backed source's every revision has. A reader
 * that needs to order or filter on that interval (`recorded-read-service.ts`'s
 * point-read and scan `ORDER BY`) consults this instead of re-deriving the
 * fact from `binding.kind` itself, so a fourth binding kind cannot leave one
 * site still assuming a column the new binding's source does not have.
 */
export type RecordedReadSource = Readonly<{
  source: (
    table: RecordedSourceTable,
    revision: RecordedInstantParts,
  ) => SqlFragment;
  predicate: (
    prefix: SqlFragment,
    revision: RecordedInstantParts,
  ) => SqlFragment | undefined;
  carriesInterval: boolean;
}>;

/**
 * `source`/`predicate` shared by every TypeGraph-relation-backed binding: the
 * recorded relation named by `schema`, narrowed by the `recorded_from <= r
 * AND r < recorded_to` interval every recorded row carries. `recordedRelation`
 * and the built-in capture binding both build their seam this way, so the
 * relation swap and the interval cannot drift apart between the two kinds.
 */
function typeGraphRelationRecordedReadSource(
  schema: SqlSchema,
): RecordedReadSource {
  return {
    source(
      table: RecordedSourceTable,
      _revision: RecordedInstantParts,
    ): SqlFragment {
      switch (table) {
        case "nodes": {
          return schema.recordedNodesTable;
        }
        case "edges": {
          return schema.recordedEdgesTable;
        }
        case "identityAssertions": {
          return schema.recordedIdentityAssertionsTable;
        }
      }
    },
    predicate(
      prefix: SqlFragment,
      revision: RecordedInstantParts,
    ): SqlFragment {
      if (revision.kind !== "typegraph") {
        // Every caller resolves `revision` by parsing the SAME coordinate this
        // binding was bound to source rows from — reaching here with an
        // engine-native (`e1`) revision would mean a coordinate minted for one
        // ownership form reached the read binding of the other, which the
        // construction-time and asOfRecorded ownership checks both refuse
        // before a read is ever compiled.
        throw new CompilerInvariantError(
          "A TypeGraph-relation recorded read binding received an engine-native revision.",
          { revisionKind: revision.kind },
        );
      }
      const recordedFrom = sql`${prefix}recorded_from`;
      const recordedTo = sql`${prefix}recorded_to`;
      const { revision: revisionNumber } = revision;
      return sql`${recordedFrom} <= ${revisionNumber} AND ${revisionNumber} < ${recordedTo}`;
    },
    carriesInterval: true,
  };
}

export type ExternalRecordedReadSource = Readonly<{
  kind: "external";
  schema: SqlSchema;
  [EXTERNAL_RECORDED_READ_SOURCE]: true;
}> &
  RecordedReadSource;

type ExternalRecordedReadSourceCandidate = Readonly<{
  kind?: unknown;
  schema?: unknown;
  [EXTERNAL_RECORDED_READ_SOURCE]?: unknown;
}>;

const TYPEGRAPH_RECORDED_READ_SOURCE: unique symbol = typeGraphGlobalSymbol(
  "typegraph-recorded-read-source-v1",
);

export type TypeGraphRecordedReadSource = Readonly<{
  kind: "typegraph-capture";
  schema: SqlSchema;
  [TYPEGRAPH_RECORDED_READ_SOURCE]: true;
}> &
  RecordedReadSource;

const ENGINE_RECORDED_READ_SOURCE: unique symbol = typeGraphGlobalSymbol(
  "engine-recorded-read-source-v1",
);

/**
 * The recorded read binding for a backend that owns recorded time itself
 * (`GraphBackend.recordedTime`, `backend/capabilities/recorded-time.ts`).
 * `source` defers to `recordedTime.source`, converting the parsed
 * engine-native revision into the `EngineRecordedRevision` shape that member
 * expects; `predicate` always returns `undefined` — the engine's own source
 * expression already scopes every row to exactly one revision, so there is no
 * separate interval to layer on top the way the TypeGraph-relation source
 * needs one.
 */
export type EngineRecordedReadSource = Readonly<{
  kind: "engine-native";
  schema: SqlSchema;
  [ENGINE_RECORDED_READ_SOURCE]: true;
}> &
  RecordedReadSource;

export type RecordedReadBinding =
  | ExternalRecordedReadSource
  | TypeGraphRecordedReadSource
  | EngineRecordedReadSource;

/**
 * Narrows a parsed recorded revision to the engine-native shape
 * `EngineRecordedTimeMembers.source`/`revisionNow` traffic in. Symmetric with
 * `typeGraphRelationRecordedReadSource`'s own guard: a TypeGraph-owned (`r1`)
 * revision reaching an engine-native binding means a coordinate minted for
 * the other ownership form got here, which construction and `asOfRecorded`
 * both refuse ahead of any read.
 */
function requireEngineRecordedRevision(
  revision: RecordedInstantParts,
): EngineRecordedRevision {
  if (revision.kind !== "engine") {
    throw new CompilerInvariantError(
      "An engine-native recorded read binding received a TypeGraph-owned revision.",
      { revisionKind: revision.kind },
    );
  }
  return { revision: revision.revision, recordedAt: revision.recordedAt };
}

/**
 * Builds the recorded read binding for an engine-native backend, wrapping its
 * `recordedTime` member as the {@link RecordedReadSource} seam every recorded
 * read consults. `schema` is carried only for shape parity with the other two
 * binding kinds (`recordedReadSqlSchema` still needs a live schema to copy the
 * non-swapped table references from) — the engine's own `source` never reads
 * from it.
 */
export function createEngineRecordedReadBinding(
  recordedTime: EngineRecordedTimeMembers,
  schema: SqlSchema,
): EngineRecordedReadSource {
  const readSchema = requireSqlSchema(
    schema,
    "engine recorded read binding schema",
  );
  return Object.freeze({
    kind: "engine-native",
    schema: readSchema,
    [ENGINE_RECORDED_READ_SOURCE]: true as const,
    source(table: RecordedSourceTable, revision: RecordedInstantParts) {
      return recordedTime.source(
        table,
        requireEngineRecordedRevision(revision),
      );
    },
    predicate(): undefined {
      return;
    },
    carriesInterval: false,
  });
}

export type RecordedRelationOptions = Readonly<{
  schema: SqlSchema;
}>;

export function recordedRelation(
  options: RecordedRelationOptions,
): ExternalRecordedReadSource {
  const schema = requireSqlSchema(options.schema, "recordedRelation schema");
  return Object.freeze({
    kind: "external",
    schema,
    [EXTERNAL_RECORDED_READ_SOURCE]: true as const,
    ...typeGraphRelationRecordedReadSource(schema),
  });
}

export function requireExternalRecordedReadSource(
  source: unknown,
): ExternalRecordedReadSource | undefined {
  if (source === undefined) return undefined;
  if (isExternalRecordedReadSource(source)) return source;
  throw new ConfigurationError(
    "recordedRead must be created with recordedRelation({ schema }).",
    { code: "INVALID_RECORDED_READ_SOURCE" },
    {
      suggestion:
        "Pass { recordedRead: recordedRelation({ schema }) } for an externally populated recorded relation. Use { history: true } when TypeGraph should capture writes.",
    },
  );
}

function isExternalRecordedReadSource(
  source: unknown,
): source is ExternalRecordedReadSource {
  if (typeof source !== "object" || source === null) return false;
  const candidate = source as ExternalRecordedReadSourceCandidate;
  return (
    candidate.kind === "external" &&
    candidate[EXTERNAL_RECORDED_READ_SOURCE] === true &&
    isSqlSchema(candidate.schema) &&
    Object.isFrozen(candidate)
  );
}

export function createRecordedReadBinding(
  schema: SqlSchema,
): TypeGraphRecordedReadSource {
  const readSchema = requireSqlSchema(schema, "recorded read binding schema");
  return Object.freeze({
    kind: "typegraph-capture",
    schema: readSchema,
    [TYPEGRAPH_RECORDED_READ_SOURCE]: true as const,
    ...typeGraphRelationRecordedReadSource(readSchema),
  });
}

export function requireRecordedReadBinding(
  binding: unknown,
  surface: string,
): RecordedReadBinding {
  if (isRecordedReadBinding(binding)) return binding;
  if (binding !== undefined) {
    throw new ConfigurationError(
      "Recorded-time reads require a recorded read relation created by TypeGraph.",
      { code: "INVALID_RECORDED_READ_BINDING", surface },
      {
        suggestion:
          "Use createStore(graph, backend, { history: true }) or { recordedRead: recordedRelation({ schema }) } instead of passing a plain object.",
      },
    );
  }
  throw new ConfigurationError(
    "Recorded-time reads require a recorded read relation.",
    { code: "RECORDED_READ_REQUIRES_BINDING", surface },
    {
      suggestion:
        "Create the store with createStore(graph, backend, { history: true }) to bind TypeGraph's built-in captured relation, or pass { recordedRead: recordedRelation({ schema }) } for an externally populated recorded relation.",
    },
  );
}

type TypeGraphRecordedReadSourceCandidate = Readonly<{
  kind?: unknown;
  schema?: unknown;
  [TYPEGRAPH_RECORDED_READ_SOURCE]?: unknown;
}>;

function isTypeGraphRecordedReadSource(
  source: unknown,
): source is TypeGraphRecordedReadSource {
  if (typeof source !== "object" || source === null) return false;
  const candidate = source as TypeGraphRecordedReadSourceCandidate;
  return (
    candidate.kind === "typegraph-capture" &&
    candidate[TYPEGRAPH_RECORDED_READ_SOURCE] === true &&
    isSqlSchema(candidate.schema) &&
    Object.isFrozen(candidate)
  );
}

type EngineRecordedReadSourceCandidate = Readonly<{
  kind?: unknown;
  schema?: unknown;
  [ENGINE_RECORDED_READ_SOURCE]?: unknown;
}>;

function isEngineRecordedReadSource(
  source: unknown,
): source is EngineRecordedReadSource {
  if (typeof source !== "object" || source === null) return false;
  const candidate = source as EngineRecordedReadSourceCandidate;
  return (
    candidate.kind === "engine-native" &&
    candidate[ENGINE_RECORDED_READ_SOURCE] === true &&
    isSqlSchema(candidate.schema) &&
    Object.isFrozen(candidate)
  );
}

function isRecordedReadBinding(
  binding: unknown,
): binding is RecordedReadBinding {
  return (
    isExternalRecordedReadSource(binding) ||
    isTypeGraphRecordedReadSource(binding) ||
    isEngineRecordedReadSource(binding)
  );
}

/**
 * Returns a schema view whose primary node/edge sources are the recorded-time
 * relations for `revision`, resolved through `binding.source` — the single
 * place the relation swap and the recorded-time predicate ({@link
 * recordedReadSchemaFor}'s caller, via `compileTemporalFilter`) both consult
 * the same seam. The recorded relations are row-compatible with the live
 * tables for every column the query compiler, subgraph extractor, and
 * algorithms already read.
 */
export function recordedReadSqlSchema(
  binding: RecordedReadBinding,
  revision: RecordedInstantParts,
): SqlSchema {
  const validated = requireRecordedReadBinding(binding, "recorded-read-schema");
  const { schema } = validated;
  return freezeSqlSchema({
    tables: schema.tables,
    nodesTable: validated.source("nodes", revision),
    edgesTable: validated.source("edges", revision),
    recordedNodesTable: schema.recordedNodesTable,
    recordedEdgesTable: schema.recordedEdgesTable,
    recordedClockTable: schema.recordedClockTable,
    revisionOriginsTable: schema.revisionOriginsTable,
    identityAssertionsTable: schema.identityAssertionsTable,
    recordedIdentityAssertionsTable: schema.recordedIdentityAssertionsTable,
    identityClosureTable: schema.identityClosureTable,
    identitySeparationTable: schema.identitySeparationTable,
    fulltextTable: schema.fulltextTable,
  });
}

/**
 * Resolves the read schema for a coordinate: the live schema when no recorded
 * pin is set, or the recorded-relation view when `recordedAsOf` is present. The
 * single place every read path decides whether to source the recorded tables.
 */
export function recordedReadSchemaFor(
  schema: SqlSchema,
  recordedAsOf: string | undefined,
  binding: RecordedReadBinding | undefined,
  surface: string,
): SqlSchema {
  const baseSchema = requireSqlSchema(schema, `${surface} schema`);
  if (recordedAsOf === undefined) return baseSchema;
  const revision = parseRecordedInstant(recordedAsOf, "recordedAsOf");
  return recordedReadSqlSchema(
    requireRecordedReadBinding(binding, surface),
    revision,
  );
}

/**
 * Default SqlSchema using standard TypeGraph table names.
 */
export const DEFAULT_SQL_SCHEMA: SqlSchema = createSqlSchema();

/**
 * The compiler's resolved view of one declared embedding field — the
 * `(dimensions, metric, indexType)` a {@link VectorStrategy} needs to
 * name and scan the field's typed per-`(kind, field)` storage. Sourced
 * from the registered node schema's `embedding()` declaration when the
 * store builds its compile options.
 */
export type VectorSlotDescriptor = Readonly<{
  dimensions: number;
  metric: VectorMetric;
  indexType: VectorIndexType;
}>;

/**
 * Map of declared embedding slots keyed by {@link vectorSlotKey} -
 * `"<nodeKind>\0<fieldPath>"` (NUL-separated). Carries every `(concrete kind,
 * fieldPath)` that declares an embedding field, so the compiler's
 * `field.similarTo(...)` CTE can UNION ALL the per-field tables for the
 * kinds in an alias that actually declare the field (only
 * `includeSubClasses` yields more than one).
 */
export type VectorSlotMap = ReadonlyMap<string, VectorSlotDescriptor>;

/** NUL-delimited composite key for {@link VectorSlotMap}. */
export function vectorSlotKey(nodeKind: string, fieldPath: string): string {
  return `${nodeKind}\u0000${fieldPath}`;
}

/**
 * CTE aliases used by the standard query emitter. Joining on these names
 * across multiple builder files is fragile when typed as raw strings —
 * import these constants instead.
 */
export const ALIAS_CTE_PREFIX = "cte_" as const;
export const EMBEDDINGS_CTE_ALIAS = "cte_embeddings" as const;
export const FULLTEXT_CTE_ALIAS = "cte_fulltext" as const;
export const HYBRID_CANDIDATES_CTE_ALIAS = "cte_relevance_candidates" as const;
